// src/components/Filter/FilterModules.jsx
import { useMemo, useCallback } from 'react';
import FilterModuleCheckbox from './FilterModuleCheckbox.jsx';
import { filterTypes } from '../../config.js';
import FilterModuleRange from './FilterModuleRange.jsx';
import { useMediaQuery } from 'react-responsive';
import SpecialsToggle from './SpecialsToggle.jsx';
import CaretToggle from './CaretToggle.jsx';
import { useFilter } from '../../contexts/FilterContext';

const FilterModules = ({
    menuFilterTerms,
    hideFilters,
    mobileDisplay,
    thcMin
}) => {
    const isMobile = useMediaQuery({ query: '(max-width: 991px)' });
    const { toggleMobileDisplayAllFilters, setToggleMobileDisplayAllFilters } = mobileDisplay;
    
    const { 
        selectedFilters,
        setSelectedFilters,
        handleFilterChange,
        handleRangeChange,
        clearAllFilters,
        menuFilter,
        specialsExist,
        closeMobileFilter
    } = useFilter();

    // Extract filter options from menuFilterTerms or fallback to filterTerms
    const {
        categories,
        brands,
        subcategories,
        strains,
        effects,
        weights,
        potency,
        tags,
    } = menuFilterTerms || filterTerms || {}; 

    // Render filter module helper function
    const renderFilterModule = useCallback((
        title,
        filterType,
        filters,
        hideFilter = false
    ) => {
        if (hideFilter || !(Array.isArray(filters) && filters.length > 0))
            return null;

        return (
            <div
                role="region"
                aria-label={`${title} filters`}
                className="filter-module-section"
            >
                <CaretToggle title={title} key={title}>
                    <FilterModuleCheckbox
                        filterType={filterType}
                        filters={filters}
                        selectedFilters={selectedFilters}
                        setSelectedFilters={setSelectedFilters}
                        handleChange={handleFilterChange}
                        title={title}
                    />
                </CaretToggle>
            </div>
        );
    }, [selectedFilters, setSelectedFilters, handleFilterChange]);

    // Button handlers
    const handleMobileApplyFiltersBtn = useCallback(() => {
        closeMobileFilter();
    }, [closeMobileFilter]);

    const handleClearFiltersBtn = useCallback(() => {
        clearAllFilters();
        if (isMobile) {
            closeMobileFilter();
        }
    }, [clearAllFilters, isMobile, closeMobileFilter]);

    // Memoize filter modules to prevent unnecessary re-renders
    const memoizedFilterModules = useMemo(() => (
        <>
            {renderFilterModule('Category', filterTypes.category, categories, hideFilters?.cat)}
            {renderFilterModule('Brand', filterTypes.brands, brands)}
            {renderFilterModule('Subcategory', filterTypes.subcategory, subcategories, hideFilters?.subcat)}
            {renderFilterModule('Strain', filterTypes.strain, strains)}
            {renderFilterModule('Effects', filterTypes.effects, effects)}
            {renderFilterModule('Weights', filterTypes.weights, weights)}
        </>
    ), [categories, brands, subcategories, strains, effects, weights, hideFilters, renderFilterModule]);

    // Memoize range modules to prevent unnecessary re-renders
    const memoizedRangeModules = useMemo(() => (
        <>
            <div role="region" aria-label="THC Potency filters" className="filter-module-section">
                <CaretToggle
                    title={'THC Potency'}
                    initiallyOpen={Boolean(thcMin)}
                >
                    <FilterModuleRange
                        filterType={filterTypes.potencyThc}
                        selectedFilters={selectedFilters}
                        handleChange={handleRangeChange}
                        filters={potency}
                        thcMin={thcMin}
                    />
                </CaretToggle>
            </div>
    
            <div role="region" aria-label="CBD Potency filters" className="filter-module-section">
                <CaretToggle title={'CBD Potency'}>
                    <FilterModuleRange
                        filterType={filterTypes.potencyCbd}
                        selectedFilters={selectedFilters}
                        handleChange={handleRangeChange}
                        filters={potency}
                    />
                </CaretToggle>
            </div>
    
            <div role="region" aria-label="Price filters" className="filter-module-section">
                <CaretToggle title={'Price'}>
                    <FilterModuleRange
                        filterType={filterTypes.price}
                        selectedFilters={selectedFilters}
                        handleChange={handleRangeChange}
                        prefix="$"
                    />
                </CaretToggle>
            </div>
        </>
    ), [selectedFilters, handleRangeChange, potency, thcMin]);

    return (
        <div
            id="headless-filter-sidebar"
            className={`headless-filter-sidebar ${isMobile ? 'headless-filter-mobile' : ''}`}
            role="group"
            aria-label="Filter options"
        >
            {specialsExist && (
                <div className="headless-desktop-filter-specials">
                    <SpecialsToggle
                        handleChange={handleFilterChange}
                        selectedFilters={selectedFilters}
                        deviceType="desktop"
                    />
                </div>
            )}

            {memoizedFilterModules}
            {memoizedRangeModules}

            {renderFilterModule('Tags', filterTypes.tags, tags, hideFilters?.tags)}

            <div className="headless-filter-sidebar-controls">
                {isMobile && (
                    <button
                        id="apply-checkbox-filters"
                        className="apply-checkbox-filters-button"
                        onClick={handleMobileApplyFiltersBtn}
                        aria-label="Apply selected filters"
                    >
                        Apply
                    </button>
                )}
                <button
                    id="clear-checkbox-filters"
                    className="clear-checkbox-filters-button"
                    onClick={handleClearFiltersBtn}
                    aria-label="Clear all filters"
                >
                    Clear All
                </button>
            </div>
        </div>
    );
};

export default FilterModules;
